import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { rechenwegText, rechneRechenweg, type Rechenweg } from '@symphenia/calc-core'
import { apiErrorText } from '@/lib/api-error'
import { inlineInput } from '@/lib/form-styles'
import { createWorkStep } from '../api/calculation-sheet.api'
import { fetchTimeStandards } from '../api/calculation-settings.api'
import type { CalcTimeStandard } from '../types/calculation.types'

const leseZahl = (t: string) => {
  const n = Number(t.replace(/\./g, '').replace(',', '.'))
  return t.trim() !== '' && Number.isFinite(n) ? n : null
}

/**
 * AP-37 Detailblatt N 6.5 — **Arbeitsschritt aus Richtwert**: Richtwert wählen, Menge der
 * Bezugsgröße eingeben — die Stunden entstehen mit Rechenweg (`n × r`), gerechnet vom Server.
 * Dieselbe Rechnung zeigt der Dialog vorab (`calc-core`).
 */
export function RichtwertDialog({
  kalkulationId,
  positionId,
  positionsEinheit,
  onClose,
  onAngelegt,
}: {
  kalkulationId: string
  positionId: string
  positionsEinheit: string | null
  onClose: () => void
  onAngelegt: () => void
}) {
  const [richtwerte, setRichtwerte] = useState<CalcTimeStandard[] | null>(null)
  const [wahl, setWahl] = useState('')
  const [menge, setMenge] = useState('')
  const [bezug, setBezug] = useState<'TOTAL' | 'PER_UNIT'>('TOTAL')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    fetchTimeStandards()
      .then((r) => {
        setRichtwerte(r)
        if (r.length === 1) setWahl(r[0].id)
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die Richtwerte ließen sich nicht laden.')))
  }, [])

  const r = richtwerte?.find((x) => x.id === wahl) ?? null
  const n = leseZahl(menge)
  const vorschau = useMemo(() => {
    if (!r || n === null) return null
    const weg: Rechenweg = {
      ausdruck: 'n * r',
      groessen: {
        n: { wert: n, einheit: r.referenceUnitCode, quelle: { art: 'EINGABE' } },
        r: { wert: r.hoursPerReference, einheit: `h/${r.referenceUnitCode}`, quelle: { art: 'RICHTWERT', id: r.id } },
      },
      bezug,
    }
    weg.ergebnis = rechneRechenweg(weg)
    return rechenwegText(weg, 'h')
  }, [r, n, bezug])

  async function anlegen() {
    if (!r || n === null) return
    setLaeuft(true)
    try {
      setFehler(null)
      await createWorkStep(kalkulationId, positionId, { timeStandardId: r.id, referenceQuantity: n, quantityBasis: bezug })
      onAngelegt()
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Arbeitsschritt ließ sich nicht anlegen.'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md space-y-3 rounded-lg border border-border p-4 shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Arbeitsschritt aus Richtwert</h2>
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={onClose} aria-label="Schließen">
            <X className="h-4 w-4" />
          </button>
        </div>
        {richtwerte && richtwerte.length === 0 && (
          <p className="text-sm text-muted-foreground">Noch keine Richtwerte — anlegen unter Einstellungen → Kalkulation → Richtwerte.</p>
        )}
        {richtwerte && richtwerte.length > 0 && (
          <>
            <label className="block text-sm">
              <span className="block text-xs text-muted-foreground">Richtwert</span>
              <select className={inlineInput} value={wahl} onChange={(e) => setWahl(e.target.value)}>
                <option value="">— wählen —</option>
                {richtwerte.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.activityName} — {x.referenceName} ({x.hoursPerReference.toLocaleString('de-DE', { maximumFractionDigits: 4 })} h/{x.referenceUnitCode})
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="block text-xs text-muted-foreground">Menge {r ? `${r.referenceName} (${r.referenceUnitCode})` : 'der Bezugsgröße'}</span>
                <input className={`${inlineInput} text-right`} inputMode="decimal" value={menge} onChange={(e) => setMenge(e.target.value)} autoFocus />
              </label>
              <label className="block text-sm">
                <span className="block text-xs text-muted-foreground">Die Menge gilt</span>
                <select className={inlineInput} value={bezug} onChange={(e) => setBezug(e.target.value as 'TOTAL' | 'PER_UNIT')}>
                  <option value="TOTAL">für die ganze Position</option>
                  <option value="PER_UNIT">je {positionsEinheit || 'Einheit'} der Position</option>
                </select>
              </label>
            </div>
            {vorschau && <p className="rounded-md bg-muted/50 px-2 py-1.5 text-sm tabular-nums">{vorschau}</p>}
          </>
        )}
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={!r || n === null || laeuft} onClick={() => void anlegen()}>
            Anlegen
          </button>
        </div>
      </div>
    </div>
  )
}
